"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAuthToken } from "@/hooks/use-auth"
import { aiClient, type ChatRequest, type GenerateRequest } from "@/lib/ai-client"

// API client for analysis persistence
const API_BASE = ""

async function getStoredAnalysis(projectId: string, token: string) {
  const response = await fetch(`${API_BASE}/api/analysis/${projectId}`, {
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  })
  if (!response.ok) {
    throw new Error("Failed to fetch stored analysis")
  }
  return response.json()
}

async function saveAnalysis(projectId: string, data: { recommendations: unknown[]; coverage?: unknown; summary?: string }, token: string) {
  const response = await fetch(`${API_BASE}/api/analysis/${projectId}`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    throw new Error("Failed to save analysis")
  }
  return response.json()
}

async function updateRecommendationStatus(
  projectId: string, 
  recommendationId: string, 
  action: 'apply' | 'dismiss' | 'edit',
  token: string
) {
  const response = await fetch(`${API_BASE}/api/analysis/${projectId}/recommendations/${recommendationId}`, {
    method: "PATCH",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ action }),
  })
  if (!response.ok) {
    throw new Error("Failed to update recommendation")
  }
  return response.json()
}

/**
 * Hook for chatting with project knowledge base
 */
export function useChat(projectId: string) {
  const token = useAuthToken()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: Omit<ChatRequest, "projectId">) => {
      return aiClient.chat({ ...request, projectId }, token || undefined)
    },
    onSuccess: () => {
      // Optionally invalidate any cached conversations
      queryClient.invalidateQueries({ queryKey: ["conversations", projectId] })
    },
  })
}

/**
 * Hook for generating content with AI agents
 */
export function useGenerateContent(projectId: string) {
  const token = useAuthToken()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: Omit<GenerateRequest, "projectId">) => {
      return aiClient.generate({ ...request, projectId }, token || undefined)
    },
    onSuccess: (_, variables) => {
      // Invalidate the relevant artifact cache
      queryClient.invalidateQueries({ queryKey: [variables.artifactType, projectId] })
    },
  })
}

/**
 * Hook for getting stored Product Guru analysis from database
 */
export function useStoredAnalysis(projectId: string) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["stored-analysis", projectId],
    queryFn: () => getStoredAnalysis(projectId, token!),
    enabled: !!projectId && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook for running Product Guru analysis and saving to database
 * - Fetches stored data on page load
 * - Only runs new analysis when user clicks "Run Analysis Now"
 */
export function useProductGuruAnalysis(projectId: string) {
  const token = useAuthToken()
  const queryClient = useQueryClient()
  
  // Get stored analysis from database
  const storedQuery = useStoredAnalysis(projectId)
  
  // Mutation to run new analysis
  const runAnalysis = useMutation({
    mutationFn: async () => {
      // Call AI service to get fresh analysis
      const aiData = await aiClient.analyze(projectId, token || undefined)
      
      // Save to database and get back the DB records with proper IDs
      const saveResult = await saveAnalysis(projectId, {
        recommendations: aiData.recommendations || [],
        coverage: aiData.coverage,
        summary: aiData.summary,
      }, token!)
      
      // Return the DB response which has correct IDs
      return saveResult.data
    },
    onSuccess: () => {
      // Refetch from database to get recommendations with DB IDs
      queryClient.invalidateQueries({ queryKey: ["stored-analysis", projectId] })
    },
  })

  // Return combined interface
  return {
    // Data from stored analysis
    data: storedQuery.data?.exists ? storedQuery.data.data : null,
    // Loading states
    isLoading: storedQuery.isLoading,
    isFetching: runAnalysis.isPending,
    isError: storedQuery.isError || runAnalysis.isError,
    // Run new analysis
    refetch: () => runAnalysis.mutateAsync(),
    // Refresh from database
    refreshFromDb: () => storedQuery.refetch(),
    // Metadata
    analyzedAt: storedQuery.data?.exists ? storedQuery.data.data.analyzedAt : null,
  }
}

/**
 * Hook for updating recommendation status (apply, dismiss, edit)
 */
export function useUpdateRecommendation(projectId: string) {
  const token = useAuthToken()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ recommendationId, action }: { recommendationId: string; action: 'apply' | 'dismiss' | 'edit' }) => {
      return updateRecommendationStatus(projectId, recommendationId, action, token!)
    },
    onSuccess: () => {
      // Refetch stored analysis to get updated statuses
      queryClient.invalidateQueries({ queryKey: ["stored-analysis", projectId] })
    },
  })
}

/**
 * Hook for uploading documents
 */
export function useUploadDocument(projectId: string) {
  const token = useAuthToken()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      return aiClient.uploadDocument({ projectId, file }, token || undefined)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents", projectId] })
    },
  })
}

/**
 * Hook for listing project documents
 */
export function useDocuments(projectId: string) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["documents", projectId],
    queryFn: () => aiClient.listDocuments(projectId, token || undefined),
    enabled: !!projectId && !!token,
  })
}

/**
 * Hook for getting document status with polling
 */
export function useDocumentStatus(documentId: string, pollInterval?: number) {
  const token = useAuthToken()

  return useQuery({
    queryKey: ["document-status", documentId],
    queryFn: () => aiClient.getDocumentStatus(documentId, token || undefined),
    enabled: !!documentId && !!token,
    refetchInterval: (query) => {
      // Stop polling once processed or failed
      const status = query.state.data?.status
      if (status === "processed" || status === "failed") {
        return false
      }
      return pollInterval || 2000
    },
  })
}

/**
 * Hook for deleting documents
 */
export function useDeleteDocument(projectId: string) {
  const token = useAuthToken()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (documentId: string) => {
      return aiClient.deleteDocument(documentId, token || undefined)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents", projectId] })
    },
  })
}

/**
 * Hook for checking AI service health
 */
export function useAIServiceHealth() {
  return useQuery({
    queryKey: ["ai-health"],
    queryFn: () => aiClient.healthCheck(),
    staleTime: 30 * 1000, // Check every 30 seconds
    retry: 1,
  })
}

